import React from 'react';
import toast from 'react-hot-toast';
import useApi from '../useApi';

export interface IIntakeFormRecord {
  _id: string;
  id: string;
  answer: string | string[] | number;
  more_details?: { [key: string]: string };
  type: string;
  text: string;
  options: Array<{
    key: string;
    name: string;
    textboxRequired: string;
  }>;
  scale?: {
    min: {
      key: string;
      name: string;
      textboxRequired: string;
    },
    max: {
      key: string;
      name: string;
      textboxRequired: string;
    }
  };
  category: {
    _id: string;
    key: string;
    name: string;
    sequence: number;
  };
}

export interface IIntakeFormCategory {
  _id: string;
  name: string;
  sequence: number;
  records: IIntakeFormRecord[];
}

interface IIntakeFormRes {
  status: 'success' | 'fail';
  data: IIntakeFormCategory[];
  msg: string;
  plan: { _id: string; name: string };
}

const useIntakeForm = ({ patientId }: { patientId?: string }) => {
  const [intakeFormData, setIntakeFormData] = React.useState<IIntakeFormCategory[]>();
  const [planId, setPlanId] = React.useState<string>('');

  const { loading: intakeFormLoading, makeApiCall } = useApi({
    url: `/rtm/template/intakeform/answer/${patientId}`,
    method: 'GET',
  });

  const [hasFetched, setHasFetched] = React.useState(false);


  React.useEffect(() => {
    setHasFetched(false);
    setIntakeFormData(undefined);
  }, [patientId]);

  const getIntakeFormData = React.useCallback(async () => {
    if (!patientId) return;
    if (hasFetched) return; 

    try {
      const { response, error } = await makeApiCall<undefined, IIntakeFormRes>();

      if (response?.status === 'success') {
        setIntakeFormData(response.data);
      } else {
        setIntakeFormData(undefined);
      }

      if (response?.plan?._id) {
        setPlanId(response.plan._id);
      }

      if (error) {
        toast.error('Something went wrong while fetching intake form data. Please try again later');
      }
      
      setHasFetched(true);
    } catch (err) {
      console.error('Error fetching intake form:', err);
      toast.error('Failed to load intake form data');
      setHasFetched(true);
    }
  }, [patientId, hasFetched, makeApiCall]);

  return {
    intakeFormData,
    getIntakeFormData,
    intakeFormLoading,
    planId,
  };
};

export default useIntakeForm;
